import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { Button, Skeleton, Spinner, Tag, Typography } from '@volvo/vcdk-react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import type { ICampaignWithSerialNumbers, IndicatorType } from '@/service/vpc/types/IAftermarketCampaign';
import type { ColumnDef } from '@tanstack/react-table';

import CampaignTypes from '@/components/CampaignTypes';
import EmptyState from '@/components/EmptyState';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import FormatDateTime from '@/components/FormatDateTime';
import InformationWithCta from '@/components/InformationWithCta';
import ReleaseNotesModal from '@/components/modals/ReleaseNotesModal';
import SoftwareInstallationAvailableModal from '@/components/modals/SoftwareInstallationAvailableModal';
import InstallAvailableButton from '@/components/remote-software/components/InstallAvailableButton';
import Table from '@/components/Table';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import useMediaQuery from '@/hooks/useMediaQuery';
import { loaderUnitCampaignsPage } from '@/loaders/unit.campaigns.loader';
import {
  getCombinedCampaignsQueryOptions,
  getIndividualProductsQueryOptions,
  unitByIdQueryOptions,
} from '@/service/vpc/queryOptions';
import {
  BLUE_DOT_STATUSES,
  HIDDEN_CAMPAIGN_STATUSES,
  PERFORMED_CAMPAIGN_STATUSES,
} from '@/service/vpc/types/IInstallableCampaign';
import { chassis, common, indicator, rsdwl } from '@/utils';
import { dealerLocatorLink } from '@/utils/baseLinks';

export function UnitCampaignsPage() {
  const { t } = useTranslation();
  const { mediaQueryNodes: mq } = useMediaQuery();
  const { userHasPermission } = useFleetAccount();
  const hasSwDownload = userHasPermission('swdownload.read');
  const { endCustomerUnitId } = useParams({
    strict: false,
  });
  const navigate = useNavigate({ from: '/fleets/$fleetId/units/$endCustomerUnitId/campaigns/' });

  const { data: unit } = useQuery(unitByIdQueryOptions({ endCustomerUnitId: endCustomerUnitId! }));
  const serialNumbers = chassis.getUnitSerialNumbers(unit) || [];

  const { data: campaigns, isLoading: isLoadingCampaigns } = useQuery(
    getCombinedCampaignsQueryOptions({ endCustomerUnitIds: [endCustomerUnitId!], serialNumbers })
  );

  const { data: productsData, isLoading: isLoadingProducts } = useQuery(
    getIndividualProductsQueryOptions({ serialNumbers: serialNumbers || [] })
  );

  const visible = campaigns?.filter(
    (_) => !HIDDEN_CAMPAIGN_STATUSES.has(_.data.concernedIndividualProductCampaignStatus)
  );

  const activeCampaigns = visible?.filter(
    (_) => !PERFORMED_CAMPAIGN_STATUSES.has(_.data.concernedIndividualProductCampaignStatus)
  );
  const performedCampaigns = visible?.filter((_) =>
    PERFORMED_CAMPAIGN_STATUSES.has(_.data.concernedIndividualProductCampaignStatus)
  );

  const getIndividualProductBySerialNumber = (serialNumber: string) => {
    return productsData?.find((product) => product.serialNumber === serialNumber);
  };

  const columnsActiveCampaigns = [
    {
      accessorFn: (row) => row.data.aftermarketCampaignTitle,
      id: 'aftermarketCampaignTitle',
      header: t(($) => $.components.table.campaigns_table.campaign),
      sortingFn: 'alphanumeric',
      meta: {
        order: 0,
      },
      cell: (info) => {
        const row = info.row.original;
        const createdDate = row.data.createdDate;
        const validTo = row.data.validTo;
        return (
          <div className="flex items-center justify-between">
            <div>
              <Typography
                variant={mq({
                  default: 'heading4',
                  md: 'caption1',
                })}
                className="text-subtle whitespace-nowrap"
              >
                {info.getValue<string>()}
              </Typography>
              <div className="flex flex-row gap-2 md:hidden">
                <FormatDateTime date={createdDate} type="short" variant="caption1" className="text-subtle" />
                -
                <FormatDateTime date={validTo} type="short" variant="caption1" className="text-subtle" />
              </div>
            </div>
            <ReleaseNotesModal
              campaignTitle={row.data.aftermarketCampaignTitle}
              campaignCode={row.data.installableCampaign?.campaignCode}
              campaignInformation={row.data.installableCampaign?.campaignInformation}
            />
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.data.concernedIndividualProductSerialNumber,
      id: 'download',
      header: t(($) => $.components.table.campaigns_table.download),
      meta: {
        order: 4,
      },
      cell: (info) => {
        if (!hasSwDownload) return null;
        const campaign = info.row.original.data;
        const showWebInstallButton = rsdwl.showWebInstallButton(campaign);

        return showWebInstallButton ? (
          isMobile ? (
            <InstallAvailableButton
              unitName={unit?.unitName || '-'}
              endCustomerUnitId={Number(endCustomerUnitId)}
              campaign={campaign}
            />
          ) : (
            <SoftwareInstallationAvailableModal campaignTitle={campaign.aftermarketCampaignTitle} />
          )
        ) : null;
      },
    },
    {
      accessorFn: (row) => row.data.createdDate,
      id: 'createdDate',
      header: t(($) => $.components.table.campaigns_table.created_date),
      sortingFn: 'datetime',
      meta: {
        hideOnMobile: true,
      },
      cell: (info) => {
        const createdDate = info.getValue<string>();
        return <FormatDateTime date={createdDate} type="short" variant="caption1" className="text-subtle" />;
      },
    },
    {
      accessorFn: (row) => row.data.validTo,
      id: 'validTo',
      header: t(($) => $.components.table.campaigns_table.valid_to),
      sortingFn: 'datetime',
      meta: {
        hideOnMobile: true,
      },
      cell: (info) => {
        const validTo = info.getValue<string>();
        return <FormatDateTime date={validTo} type="short" variant="caption1" className="text-subtle" />;
      },
    },
    {
      accessorFn: (row) => row.data.concernedIndividualProductSerialNumber,
      id: 'product',
      header: t(($) => $.components.table.campaigns_table.product),
      sortingFn: 'alphanumeric',
      meta: {
        hideOnMobile: true,
      },
      cell: (info) => {
        if (isLoadingProducts) {
          return <Skeleton variant="rounded-rectangle" height={18} width={100} />;
        }
        const serialNumber = info.getValue<string>();
        const product = getIndividualProductBySerialNumber(serialNumber);
        return (
          <Typography variant="caption1" className="text-subtle">
            {product?.productDesignation || ''}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.data.aftermarketCampaignType,
      id: 'aftermarketCampaignType',
      header: t(($) => $.components.table.campaigns_table.type),
      meta: {
        order: 3,
      },
      cell: (info) => {
        if (isLoadingCampaigns) {
          return <Skeleton variant="rounded-rectangle" height={18} width={150} />;
        }
        const campaignType = info.getValue<string>();
        const uniqueCampaigns = Array.from(new Set(campaignType.split('_'))) as IndicatorType[];
        const status = info.row.original.data.installableCampaign?.status;
        const visibleCampaigns = uniqueCampaigns.filter(
          (type) => type !== 'Installation' || (hasSwDownload && BLUE_DOT_STATUSES.has(status ?? ('' as never)))
        );
        return (
          <div className="flex gap-4">
            {visibleCampaigns.map((campaign) => (
              <Tag key={common.slugify(campaign)} variant={indicator.getSubtleVariant(campaign)}>
                {campaign}
              </Tag>
            ))}
          </div>
        );
      },
    },
  ] as ColumnDef<ICampaignWithSerialNumbers>[];

  const columnsPerformedCampaigns = [
    {
      accessorFn: (row) => row.data.aftermarketCampaignTitle,
      id: 'aftermarketCampaignTitle',
      header: t(($) => $.components.table.campaigns_table.campaign),
      sortingFn: 'alphanumeric',
      meta: {
        order: 0,
      },
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>()}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.data.concernedIndividualProductSerialNumber,
      id: 'product',
      header: t(($) => $.components.table.campaigns_table.product),
      sortingFn: 'alphanumeric',
      meta: {
        hideOnMobile: true,
      },
      cell: (info) => {
        const serialNumber = info.getValue<string>();
        const product = getIndividualProductBySerialNumber(serialNumber);
        return (
          <Typography variant="caption1" className="text-subtle">
            {product?.productDesignation || serialNumber}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.data.aftermarketCampaignType,
      id: 'aftermarketCampaignType',
      header: t(($) => $.components.table.campaigns_table.type),
      meta: {
        order: 3,
      },
      cell: (info) => {
        if (isLoadingCampaigns) {
          return <Skeleton variant="rounded-rectangle" height={18} width={150} />;
        }
        const campaignType = info.getValue<string>();
        const uniqueCampaigns = Array.from(new Set(campaignType.split('_'))) as IndicatorType[];
        const status = info.row.original.data.installableCampaign?.status;
        const visibleCampaigns = uniqueCampaigns.filter(
          (type) => type !== 'Installation' || (hasSwDownload && BLUE_DOT_STATUSES.has(status ?? ('' as never)))
        );
        return (
          <div className="flex gap-4">
            {visibleCampaigns.map((campaign) => (
              <Tag key={common.slugify(campaign)} variant={indicator.getSubtleVariant(campaign)}>
                {campaign}
              </Tag>
            ))}
          </div>
        );
      },
    },
  ] as ColumnDef<ICampaignWithSerialNumbers>[];

  const mqSize = mq({
    default: 'mobile',
    md: 'desktop',
  });

  if (isLoadingCampaigns || !activeCampaigns || !performedCampaigns) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (activeCampaigns.length === 0 && performedCampaigns.length === 0) {
    return <EmptyState title="All up to date" message="There are no active campaigns for your units right now." />;
  }

  return (
    <>
      <div className="flex flex-col gap-9">
        <InformationWithCta
          storageKey="Campaigns_InformationWithCta"
          heading={t(($) => $.components.cta.action_cta.heading)}
          variant="info"
          closable={true}
          actions={
            <>
              <Button variant="secondary" href={dealerLocatorLink} target="_blank" rel="noopener noreferrer">
                {t(($) => $.components.cta.action_cta.find_dealer)}
              </Button>
              <Button variant="tertiary" onClick={() => navigate({ to: '/support' })}>
                {t(($) => $.components.cta.action_cta.learn_more)}
              </Button>
            </>
          }
        >
          <Typography>{t(($) => $.components.cta.action_cta.message)}</Typography>
        </InformationWithCta>
        <Table
          key={`${mqSize}-active`}
          isLoading={isLoadingCampaigns}
          data={activeCampaigns || []}
          columns={columnsActiveCampaigns}
          headerTitle={t(($) => $.pages.unit.campaigns.active_table.title)}
          showPagination={false}
          showSearch={false}
          emptyState={{
            title: t(($) => $.pages.unit.campaigns.active_table.empty_state.title),
          }}
        />
        <Table
          key={`${mqSize}-performed`}
          isLoading={isLoadingCampaigns}
          columns={columnsPerformedCampaigns}
          data={performedCampaigns || []}
          headerTitle={t(($) => $.pages.unit.campaigns.performed_table.title)}
          showPagination={false}
          showSearch={false}
          emptyState={{
            title: t(($) => $.pages.unit.campaigns.performed_table.empty_state.title),
          }}
        />
        <CampaignTypes />
      </div>
    </>
  );
}

export const Route = createFileRoute('/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/campaigns/')({
  component: UnitCampaignsPage,
  errorComponent: ErrorBoundary,
  loader: loaderUnitCampaignsPage,
});
